/**
 * 正式四表导出骨架（从 e:\\bc\\_official_xlsx 样表提炼）
 * - 药剂出库：邻水/景洪列结构；其余站由目录动态生成
 * - 实验日报：按站多级表头；未知站回退 PROCESS_DAILY_GROUPS
 * - 水量日统计 / 结算：仅骨架（系统暂无进/出水量与结算采集）
 */

export const SITE_KEY_DISPLAY = {
  default: '邻水',
  jinghong: '景洪',
};

/** 药剂出库正式列（样表 邻水 / 景洪）；cols 含生产/研发子列 */
export const DRUG_OUT_BY_SITE = {
  邻水: [
    { name: '硫酸亚铁', cols: ['生产出库'] },
    { name: '双氧水', cols: ['生产出库'] },
    { name: '氢氧化钠', cols: ['生产出库'] },
    { name: 'PAM', cols: ['生产出库'] },
    { name: '葡萄糖', cols: ['生产出库'] },
    { name: '消泡剂', cols: ['生产出库'] },
    { name: '液体碳源', cols: ['生产出库'] },
    { name: '活性碳', cols: ['生产出库'] },
  ],
  /** 与景洪出入库盘点表列序一致（目录优先时仍按目录） */
  景洪: [
    { name: '硫酸亚铁', cols: ['生产出库'] },
    { name: '双氧水', cols: ['生产出库'] },
    { name: '氢氧化钠', cols: ['生产出库'] },
    { name: '阴离子 PAM', cols: ['生产出库'] },
    { name: '阳离子 PAM', cols: ['生产出库'] },
    { name: 'PAC', cols: ['生产出库'] },
    { name: '葡萄糖', cols: ['生产出库'] },
    { name: '生物碳源', cols: ['生产出库'] },
    { name: '消泡粉', cols: ['生产出库'] },
  ],
};

/**
 * 网页/小程序默认处理日报工序（未知站回退）
 * name 对应 water_lab item.name
 */
export const PROCESS_DAILY_GROUPS = [
  {
    title: '原水',
    cols: [
      { name: 'raw_NH3N', label: '氨氮' },
      { name: 'raw_COD', label: 'COD' },
      { name: 'raw_TN', label: '总氮' },
    ],
  },
  {
    title: '一级反硝化',
    cols: [
      { name: 'd1_NH3N', label: '氨氮' },
      { name: 'd1_COD', label: 'COD' },
    ],
  },
  {
    title: '一级硝化',
    cols: [
      { name: 'n1_NH3N', label: '氨氮' },
      { name: 'n1_COD', label: 'COD' },
      { name: 'n1_SV30', label: 'SV30' },
    ],
  },
  {
    title: '二级硝化',
    cols: [
      { name: 'n2_NH3N', label: '氨氮' },
      { name: 'n2_COD', label: 'COD' },
      { name: 'n2_SV30', label: 'SV30' },
    ],
  },
  {
    title: '二级好氧',
    cols: [
      { name: 'ae2_NH3N', label: '氨氮' },
      { name: 'ae2_COD', label: 'COD' },
    ],
  },
  {
    title: '总排放口',
    cols: [
      { name: 'out_NH3N', label: '氨氮' },
      { name: 'out_COD', label: 'COD' },
      { name: 'out_TN', label: '总氮' },
    ],
  },
  { title: '调节池空高(m)', cols: [{ name: 'tank_freeboard_m', label: '空高' }] },
  { title: '备注', cols: [{ name: 'remarks', label: '内容' }] },
];

/** 将样表工序组（仅 label）转为导出组；能映射到系统字段的补 name */
function labGroupsFromLabels(groups) {
  return (groups || []).map((g) => ({
    title: g.title,
    cols: (g.cols || []).map((label) => {
      const lab = String(label || '').trim();
      const name = inferLabFieldName(g.title, lab);
      return name ? { name, label: lab || g.title } : { label: lab || g.title };
    }),
  }));
}

/** 常见工序标题别名 → 系统 section 前缀 */
const GROUP_ALIAS_TO_PREFIX = {
  原水: 'raw',
  '原水/收集池': 'raw',
  一级反硝化: 'd1',
  一级硝化: 'n1',
  二级硝化: 'n2',
  二级好氧: 'ae2',
  总排放口: 'out',
  尾水: 'out',
  '调节池空高（M)': 'tank',
  '调节池空高（M）': 'tank',
  '调节池空高(m)': 'tank',
  备注: 'remarks',
};

const LABEL_TO_SUFFIX = {
  氨氮: 'NH3N',
  COD: 'COD',
  总氮: 'TN',
  SV30: 'SV30',
  空高: 'freeboard_m',
  内容: '',
};

function inferLabFieldName(groupTitle, label) {
  const title = String(groupTitle || '').trim();
  const lab = String(label || '').trim();
  if (/调节池空高/i.test(title) || lab === '空高') return 'tank_freeboard_m';
  if (title === '备注' || lab === '内容' || lab === '备注') return 'remarks';
  const prefix = GROUP_ALIAS_TO_PREFIX[title];
  if (!prefix || prefix === 'tank' || prefix === 'remarks') return null;
  const suffix = LABEL_TO_SUFFIX[lab];
  if (!suffix) return null;
  return `${prefix}_${suffix}`;
}

/**
 * 正式实验日报表头（04_lab.xlsx 各站）
 * 邻水/景洪样表中无对应页 → 走 PROCESS_DAILY_GROUPS
 */
export const LAB_GROUPS_BY_SITE = {
  芷江: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: '一级反硝化', cols: ['氨氮', 'COD'] },
    { title: '一级硝化', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级硝化', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级好氧', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
    { title: '备注', cols: [''] },
  ]),
  麻阳: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: '一级厌氧', cols: ['氨氮', 'COD'] },
    { title: '一级好氧2池', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级好氧4池', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级好氧', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '碳源投加', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
  ]),
  会同: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: '一级缺氧', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级好氧1池', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级好氧4池', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级芬顿', cols: ['氨氮', 'COD'] },
    { title: '二级好氧', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '碳源投加', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
  ]),
  靖州: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: 'SBR', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级芬顿', cols: ['氨氮', 'COD'] },
    { title: '好氧', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '碳源投加', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
  ]),
  通道: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: '一级厌氧', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级好氧1池', cols: ['氨氮', 'SV30'] },
    { title: '一级好氧2池', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级芬顿', cols: ['氨氮', 'COD'] },
    { title: '二级好氧', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
  ]),
  怀化: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: 'A3/B3', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '四好', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级芬顿', cols: ['氨氮', 'COD'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '碳源投加', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
  ]),
  辰溪: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: '一级反硝化', cols: ['氨氮', 'COD'] },
    { title: '一级硝化', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级芬顿', cols: ['氨氮', 'COD'] },
    { title: '二级反硝化', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级好氧', cols: ['氨氮', 'COD'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
    { title: '备注', cols: [''] },
  ]),
  新晃: labGroupsFromLabels([
    { title: '原水', cols: ['氨氮', 'COD', '总氮'] },
    { title: '一级反硝化', cols: ['氨氮', 'COD'] },
    { title: '一级硝化', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '一级芬顿', cols: ['氨氮', 'COD'] },
    { title: '二级硝化', cols: ['氨氮', 'COD', 'SV30'] },
    { title: '二级好氧', cols: ['氨氮', 'COD'] },
    { title: '二级芬顿', cols: ['氨氮', 'COD'] },
    { title: '总排放口', cols: ['氨氮', 'COD', '总氮'] },
    { title: '生化加碱', cols: [''] },
    { title: '调节池空高（M)', cols: [''] },
    { title: '备注', cols: [''] },
  ]),
};

export function resolveSiteDisplayName(siteKey, cityName) {
  const fromKey = SITE_KEY_DISPLAY[String(siteKey || '').trim()];
  if (fromKey) return fromKey;
  const n = String(cityName || '').trim();
  return n || String(siteKey || '').trim() || '未知站点';
}

/**
 * 药剂列：优先站点正式样表；若有目录则按目录生成（每药剂：生产出库+研发出库）
 * 目录优先于样表固定列，保证与系统药剂目录一致。
 */
export function resolveDrugOutColumns(siteDisplayName, catalog) {
  if (Array.isArray(catalog) && catalog.length > 0) {
    return catalog.map((c) => ({
      name: String(c.drugName || '').trim() || '—',
      cols: ['生产出库', '研发出库'],
      unit: String(c.unit || '').trim(),
    }));
  }
  const fixed = DRUG_OUT_BY_SITE[siteDisplayName];
  if (fixed) {
    return fixed.map((d) => ({
      name: d.name,
      cols: [...d.cols],
    }));
  }
  return [];
}

export function resolveLabGroups(siteDisplayName) {
  const groups = LAB_GROUPS_BY_SITE[siteDisplayName];
  if (groups && groups.length) return groups;
  return PROCESS_DAILY_GROUPS;
}

/** 水量日统计：表头行（单站骨架） */
export function buildWaterDailySkeletonRows(siteDisplayName, viewYm) {
  const [yyStr, mmStr] = String(viewYm || '').split('-');
  const yy = Number(yyStr);
  const mm = Number(mmStr);
  const daysInMonth = new Date(yy, mm, 0).getDate();
  const dayCols = [];
  for (let d = 1; d <= daysInMonth; d += 1) dayCols.push(d);

  const title = `运营处处理水量统计表（${yy}年${mm}月）`;
  const header = ['序号', '', '运营处', '达标水量', '日期', ...dayCols, '合计总量'];
  const inRow = [1, '', siteDisplayName, '', '进水量', ...dayCols.map(() => ''), ''];
  const outRow = ['', '', '', '', '出水量', ...dayCols.map(() => ''), ''];

  return [
    [title],
    ['备注：系统尚未采集进/出水量数据；本表仅为与正式「运营处理水量日统计表」一致的空模板骨架。'],
    header,
    inRow,
    outRow,
  ];
}

/** 水量结算：年×站骨架（单站） */
export function buildWaterSettleSkeletonRows(siteDisplayName, viewYm) {
  const yy = Number(String(viewYm || '').slice(0, 4)) || new Date().getFullYear();
  const rows = [
    [`${yy}年度运营项目水量统计表（结算数据）— ${siteDisplayName}`],
    ['备注：系统尚未采集渗滤液进/出水结算等数据；本表仅为与正式结算表一致的空模板骨架。'],
    ['运营点      月份', siteDisplayName, siteDisplayName],
    ['', '渗滤液进水结算', '渗滤液出水结算'],
  ];
  for (let m = 1; m <= 12; m += 1) {
    rows.push([m, '', '']);
  }
  return rows;
}
